import { useEffect, useMemo, useState, useRef } from 'react'
import { toast } from 'react-toastify'
import { ESTADO_PUBLICADA } from '../../constants/publicaciones.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import ListaVentaFormModal from '../reutilizables/listaVenta/ListaVentaFormModal'
import {
  buildListaVentaInitialValues,
  validateListaVentaForm
} from '../reutilizables/listaVenta/listaVentaFormUtils'
import {
  buscarListaPorVendedor,
  actualizar as actualizarLista,
  insertarLista
} from '../servicios/listaVentaService.js'
import { BuscarPublicacionesPorLista } from '../servicios/publicacionesService'
import { ItemLista } from './ItemLista'
import { ListaContenido } from './ListaContenido'
import styles from './ListaVenta.module.css'
import { ModalEditarItemsLista } from './ModalEditarItemsLista'
import { useListaFiltros } from './useListaFiltros'

const PAGINA_ITEMS = 15
const DATE_ONLY_LENGTH = 10

export const ListaVenta = () => {
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(PAGINA_ITEMS)
  const [listaVentas, setListaVentas] = useState([])
  const [listaVentasActual, setListaVentasActual] = useState([])
  const [showModalItems, setShowModalItems] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [formValues, setFormValues] = useState(buildListaVentaInitialValues())
  const [formErrores, setFormErrores] = useState({})
  const [publicacionesPreview, setPublicacionesPreview] = useState([])
  const [cargandoPublicacionesPreview, setCargandoPublicacionesPreview] = useState(false)
  const [listaEditandoId, setListaEditandoId] = useState(null)
  const [listaSeleccionada, setListaSeleccionada] = useState(null)
  const topRef = useRef(null)
  const [actualizar, setActualizar] = useState(false)

  const idVendedor = parseInt(localStorage.getItem('idUsuario'))

  const listaOrdenadaPorCantidad = useMemo(() => {
    return [...listaVentas].sort((a, b) => {
      const ca = a.cantidadItems ?? 0
      const cb = b.cantidadItems ?? 0
      return cb - ca
    })
  }, [listaVentas])

  const {
    listaFiltradaOrdenada,
    busqueda,
    setBusqueda,
    handleBusqueda,
    handleOrdenarAZ,
    handleOrdenarPorVencimiento
  } = useListaFiltros(listaOrdenadaPorCantidad, 'nombre')

  const traerInfo = async () => {
    try {
      const { data } = await buscarListaPorVendedor(idVendedor)

      const contarItems = (lista) => {
        const countFields = [
          lista.cantidadItems,
          lista.cantItems,
          lista.totalItems,
          lista.totalPublicaciones,
          lista.total
        ]

        const hasCountField = countFields.some((v) => v !== undefined && v !== null)
        const num = Number(countFields.find((v) => v !== undefined && v !== null) ?? 0)

        const arrays = [lista.grilla, lista.items, lista.publicaciones]
        const arraysWithData = arrays.filter((a) => Array.isArray(a))
        const maxArray = arraysWithData.reduce((acc, arr) => Math.max(acc, arr.length), 0)
        const hasArray = arraysWithData.length > 0

        if (!hasCountField && !hasArray) {
          return null
        }

        return Math.max(num, maxArray)
      }

      const todosLosItems = (data || []).map((lista) => {
        const cantidadItems = contarItems(lista)
        return {
          nombre: lista.nombre,
          nombreLista: lista.nombre,
          vigenciaDesde: lista.vigenciaDesde,
          vigenciaHasta: lista.vigenciaHasta,
          ofertaDesde: lista.ofertaDesde,
          ofertaHasta: lista.ofertaHasta,
          porcDcto: lista.porcDcto,
          idListaVenta: lista.idListaVenta,
          idListaVentaEstado: lista.idListaVentaEstado,
          idVendedor: lista.idVendedor ?? idVendedor,
          signo: lista.signo,
          variacionPrecio: lista.variacionPrecio,
          variacionPrecioDesde: lista.variacionPrecioDesde,
          variacionPrecioHasta: lista.variacionPrecioHasta,
          cantidadItems
        }
      })

      const faltantes = todosLosItems.filter((l) => l.cantidadItems === null)
      if (faltantes.length > 0) {
        const conteos = await Promise.all(
          faltantes.map(async (l) => {
            const { data: pubs } = await BuscarPublicacionesPorLista(l.idListaVenta)
            const cant = Array.isArray(pubs) ? pubs.length : 0
            return { idListaVenta: l.idListaVenta, cantidadItems: cant }
          })
        )

        const conteosMap = new Map(conteos.map((c) => [c.idListaVenta, c.cantidadItems]))
        const ajustadas = todosLosItems.map((l) =>
          conteosMap.has(l.idListaVenta)
            ? { ...l, cantidadItems: conteosMap.get(l.idListaVenta) }
            : l
        )
        setListaVentas(ajustadas)
      } else {
        setListaVentas(todosLosItems)
      }
    } catch (error) {
      const mensaje = 'Ocurrió un error al cargar los items de venta.'
      toastErrorLog(error, mensaje, 'listaVenta')
    }
  }

  useEffect(() => {
    if (idVendedor) {
      traerInfo()
    }
  }, [idVendedor, actualizar])

  const editarLista = async (item) => {
    const fechaHoy = new Date().toISOString().slice(0, DATE_ONLY_LENGTH)

    setEditMode(true)
    setCargandoPublicacionesPreview(true)
    setPublicacionesPreview([])
    setListaEditandoId(item.idListaVenta)
    setFormValues({
      idListaVenta: item.idListaVenta,
      idVendedor,
      idListaVentaEstado: item.idListaVentaEstado || ESTADO_PUBLICADA,
      nombre: item.nombre || '',
      vigenciaDesde: fechaHoy,
      vigenciaHasta: item.vigenciaHasta ? item.vigenciaHasta.slice(0, DATE_ONLY_LENGTH) : '',
      ofertaDesde: item.ofertaDesde || null,
      ofertaHasta: item.ofertaHasta || null,
      porcDcto: item.porcDcto ?? null
    })
    setFormErrores({})
    setShowFormModal(true)

    try {
      const { data } = await BuscarPublicacionesPorLista(item.idListaVenta)
      const publicaciones = (data || []).map((publicacion, index) => ({
        idPublicacion: publicacion.idPublicacion ?? index,
        titulo: publicacion.titulo || `Publicacion ${index + 1}`,
        vigenciaHastaActual: publicacion.hasta || publicacion.vigenciaHasta || null
      }))

      setPublicacionesPreview(publicaciones)
    } catch (error) {
      toastErrorLog(error, 'No se pudo cargar la vista previa de publicaciones.', 'listaVenta')
      setPublicacionesPreview([])
    } finally {
      setCargandoPublicacionesPreview(false)
    }
  }

  const handleVerItems = (lista) => {
    setListaSeleccionada(lista)
    setShowModalItems(true)
  }

  const handleActualizarItems = () => {
    setActualizar((prev) => !prev)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))

    if (formErrores[name]) {
      setFormErrores((prev) => {
        const nuevos = { ...prev }
        delete nuevos[name]
        return nuevos
      })
    }
  }

  const cerrarFormModal = () => {
    setShowFormModal(false)
    setEditMode(false)
    setListaEditandoId(null)
    setFormValues(buildListaVentaInitialValues())
    setFormErrores({})
    setPublicacionesPreview([])
    setCargandoPublicacionesPreview(false)
  }

  const handleConfirmModal = async () => {
    const nuevosErrores = validateListaVentaForm({
      values: formValues,
      listas: listaVentas,
      editMode,
      currentId: listaEditandoId
    })

    setFormErrores(nuevosErrores)
    if (Object.keys(nuevosErrores).length > 0) {
      return false
    }

    const snapshotValues = { ...formValues }
    const snapshotEditMode = editMode

    cerrarFormModal()

    try {
      if (snapshotEditMode) {
        await handleActualizar(snapshotValues)
      } else {
        await handleInsertar(snapshotValues)
      }
      return true
    } catch (error) {
      const mensaje = snapshotEditMode
        ? 'Ocurrió un error al actualizar la lista.'
        : 'Ocurrió un error al crear la lista.'
      toastErrorLog(error, mensaje, 'listaVenta')
      return false
    }
  }

  const abrirModal = () => {
    setEditMode(false)
    setListaEditandoId(null)
    setPublicacionesPreview([])
    setCargandoPublicacionesPreview(false)
    setFormValues({
      ...buildListaVentaInitialValues(),
      idVendedor,
      idListaVentaEstado: ESTADO_PUBLICADA
    })
    setFormErrores({})
    setShowFormModal(true)
  }

  const handleInsertar = async (values) => {
    const nuevaLista = {
      idVendedor: values.idVendedor,
      idListaVentaEstado: values.idListaVentaEstado,
      nombre: values.nombre,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      ofertaDesde: values.ofertaDesde || null,
      ofertaHasta: values.ofertaHasta || null,
      porcDcto: values.porcDcto ? Number(values.porcDcto) : null
    }

    await insertarLista(nuevaLista)
    setActualizar((prev) => !prev)
    toast.success('Lista creada correctamente.')
  }

  const handleActualizar = async (values) => {
    const listaActualizada = {
      idListaVenta: values.idListaVenta,
      idVendedor: values.idVendedor,
      idListaVentaEstado: values.idListaVentaEstado,
      nombre: values.nombre,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      ofertaDesde: values.ofertaDesde || null,
      ofertaHasta: values.ofertaHasta || null,
      porcDcto: values.porcDcto ? Number(values.porcDcto) : null
    }

    await actualizarLista(listaActualizada)
    setActualizar((prev) => !prev)
    toast.success('Lista actualizada correctamente.')
  }

  useEffect(() => {
    const indiceUltimoItem = paginaActual * itemsPorPagina
    const indicePrimerItem = indiceUltimoItem - itemsPorPagina
    setListaVentasActual(listaFiltradaOrdenada.slice(indicePrimerItem, indiceUltimoItem))
  }, [listaFiltradaOrdenada, paginaActual, itemsPorPagina])

  const totalPaginas = Math.ceil(listaFiltradaOrdenada.length / itemsPorPagina)

  return (
    <ListaContenido
      containerClassName={styles.listaVentaContainer}
      topRef={topRef}
      titulo="Listas Venta"
      busqueda={busqueda}
      setBusqueda={setBusqueda}
      onBusqueda={(valor) => {
        handleBusqueda(valor)
        setPaginaActual(1)
      }}
      onOrdenarAZ={(dir) => {
        handleOrdenarAZ(dir)
        setPaginaActual(1)
      }}
      onOrdenarPorVencimiento={() => {
        handleOrdenarPorVencimiento()
        setPaginaActual(1)
      }}
      onAgregarLista={abrirModal}
      labelAgregar="Agregar lista"
      items={listaVentasActual}
      renderItem={(item, index) => (
        <div className="col-12 col-md-4 mb-3" key={`${item.idListaVenta ?? item.idLista ?? index}`}>
          <ItemLista
            lista={item}
            esVenta={true}
            onEditar={() => editarLista(item)}
            onVerItems={handleVerItems}
          />
        </div>
      )}
      onConfirmModal={handleConfirmModal}
      onChangeModal={handleChange}
      modalExtra={
        <>
          <ListaVentaFormModal
            show={showFormModal}
            title={editMode ? 'Editar lista de venta' : 'Crear lista de venta'}
            values={formValues}
            errores={formErrores}
            editMode={editMode}
            publicacionesPreview={publicacionesPreview}
            cargandoPublicacionesPreview={cargandoPublicacionesPreview}
            onChange={handleChange}
            onClose={cerrarFormModal}
            onConfirm={handleConfirmModal}
          />
          <ModalEditarItemsLista
            show={showModalItems}
            onHide={() => setShowModalItems(false)}
            listaVenta={listaSeleccionada}
            idVendedor={idVendedor}
            onActualizar={handleActualizarItems}
          />
        </>
      }
      totalPaginas={totalPaginas}
      paginaActual={paginaActual}
      onCambioPagina={(num) => {
        setPaginaActual(num)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
      itemsPorPagina={itemsPorPagina}
      onCambioItemsPorPagina={(num) => {
        setItemsPorPagina(num)
        setPaginaActual(1)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
    />
  )
}

export default ListaVenta
